import { Add, Check, Clear, Edit, Error } from '@mui/icons-material'
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useEditMode } from '../../store/redux/hooks'
import { Cell, HoursInterval, ScheduleTableVariant as Variant, WorkDay } from '../../store/types'
import DateFormat from '../../store/types/enums/DateFormat'
import { getPlannedCells, hasBorder, isWeekday } from '../../store/utils'
import { calculateTimeDifference } from '../../store/utils/calculatePlannedHours'
import { getSummaryCells, getTrackedCells } from '../../store/utils/getScheduleTableCells'
import { StyledTableCell, StyledTableCellRound, StyledTableRow } from '../../styles/TableStyles'
import ScheduleTableRow from './ScheduleTable.tsx/ScheduleTableRow'
import { areIntervalsValid } from './utils'
import AnimatedScheduleCollapse from './AnimatedScheduleCollapse'

export const calculateIntervalHours = (from: string, to: string) => {
  const { hoursDifference, minutesDifference } = calculateTimeDifference(from, to)
  if (hoursDifference <= 0 && minutesDifference <= 0) {
    return '-'
  }
  if (hoursDifference === 0) {
    return minutesDifference + 'm'
  }

  return hoursDifference + 'h ' + (minutesDifference === 0 ? '' : minutesDifference + 'm')
}

export const calculateAllHours = (intervals: HoursInterval[]) => {
  let totalMinutes = 0

  intervals.forEach(interval => {
    const { hoursDifference, minutesDifference } = calculateTimeDifference(interval.from, interval.to)
    totalMinutes += hoursDifference * 60 + minutesDifference
  })

  const totalHours = Math.floor(totalMinutes / 60)
  const remainingMinutes = totalMinutes % 60
  return totalHours > 0
    ? remainingMinutes > 0
      ? `${totalHours}h ${remainingMinutes}m`
      : `${totalHours}h`
    : remainingMinutes > 0
    ? `${remainingMinutes}m`
    : '-'
}

export const calculateAllHoursPerDay = (intervals: HoursInterval[], worklogHours: WorkDay | undefined) => {
  let workHours = '-'
  let allHours = calculateAllHours(intervals)
  if (worklogHours !== undefined) {
    workHours = calculateWorklogHours(worklogHours)
  }
  if (worklogHours === undefined) {
    return allHours
  }
  return workHours
}

export const calculateWorklogHours = (worklogHours: WorkDay | undefined) => {
  let totalMinutes = 0

  worklogHours?.entries.forEach(workEntry => {
    totalMinutes += workEntry.hours * 60
  })

  let minutes = Math.floor(totalMinutes)
  const seconds = (totalMinutes - minutes) * 60

  if (seconds > 0) {
    minutes += 1
  }

  const totalHours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  return totalHours > 0
    ? remainingMinutes > 0
      ? `${totalHours}h ${remainingMinutes}m`
      : `${totalHours}h`
    : remainingMinutes > 0
    ? `${remainingMinutes}m`
    : '-'
}

type ScheduleTableSectionProps = {
  date: string
  intervals: HoursInterval[]
  isOpen: boolean
  isSelected: boolean
  isEditable: boolean
  onClick: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLTableRowElement>) => void
  onClickAway: () => void
  addInterval: () => void
  deleteInterval: (intervalId: string) => void
  editInterval: (intervalId: string, date: string, newTime: string, isFromHour: boolean) => void
  worklogHours: WorkDay | undefined
  isPlannedHours: boolean
  variant: Variant
  isTogglable?: boolean
  isReadOnly?: boolean
  setIsEditable: React.Dispatch<React.SetStateAction<boolean>>
  save: () => void
  cancel: () => void
}

const ScheduleTableSectionComponent = ({
  date,
  intervals,
  isOpen,
  isSelected,
  isEditable,
  onClick,
  onClickAway,
  addInterval,
  deleteInterval,
  editInterval,
  setIsEditable,
  worklogHours,
  isPlannedHours,
  variant,
  isTogglable = false,
  isReadOnly = false,
  save,
  cancel,
}: ScheduleTableSectionProps) => {
  const workHours = useMemo(() => calculateAllHoursPerDay(intervals, worklogHours), [intervals, worklogHours])
  const allHours = useMemo(() => calculateAllHours(intervals), [intervals])
  const isBadSchedule = useMemo(
    () => worklogHours !== undefined && workHours !== allHours,
    [workHours, allHours, worklogHours]
  )
  const cells = useMemo<Record<Variant, Cell[]>>(
    () => ({
      planned: getPlannedCells(date, intervals, allHours),
      tracked: getTrackedCells(date, intervals, allHours, isSelected, isBadSchedule, workHours),
      summary: getSummaryCells(date, worklogHours),
    }),
    [date, intervals, allHours, isSelected, isBadSchedule, workHours, worklogHours]
  )
  const isSaveEnabled = useMemo(() => areIntervalsValid(intervals), [intervals])

  const { t } = useTranslation()

  const timePickerRef = useRef<HTMLInputElement>(null)

  const { setIsEditMode } = useEditMode()

  const handleEdit = () => {
    setIsEditable(true)
    setIsEditMode(true)
  }

  const handleCancel = () => {
    cancel()
  }

  const handleSave = () => {
    save()
  }

  const handleAddInterval = () => {
    addInterval()
  }

  useEffect(() => {
    if (timePickerRef.current && isEditable) {
      timePickerRef.current.focus()
    }
  }, [isEditable])

  const preventNextClickRef = useRef(false)
  const preventNextClickTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (preventNextClickTimeout.current) {
        clearTimeout(preventNextClickTimeout.current)
      }
    }
  }, [])

  const isToTimeValid = (interval: HoursInterval): boolean => {
    const from = dayjs(interval.from, DateFormat.HourMinute)
    let to = dayjs(interval.to, DateFormat.HourMinute)

    if (!to.isValid()) {
      return false
    }

    const midnight = dayjs('00:00', 'HH:mm')
    if (to.isSame(midnight, 'minute')) {
      to = to.add(1, 'day')
    }

    return to.isAfter(from)
  }

  const handleToggleRowClick = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLTableRowElement>) => {
    if (preventNextClickRef.current) {
      e.preventDefault()
      preventNextClickRef.current = false
      return
    }
    onClick(e)
  }

  const handleTouchClose = (e: React.TouchEvent<HTMLTableRowElement>) => {
    e.preventDefault()
    preventNextClickRef.current = true
    if (preventNextClickTimeout.current) {
      clearTimeout(preventNextClickTimeout.current)
    }
    preventNextClickTimeout.current = setTimeout(() => {
      preventNextClickRef.current = false
    }, 300)
    onClickAway()
  }

  return (
    <>
      {!isOpen ? (
        <>
          <ScheduleTableRow
            cells={cells[variant]}
            onClick={handleToggleRowClick}
            isWeekday={isWeekday(date)}
            isSelected={isSelected}
            isTogglable={isTogglable}
            isEditable={isEditable}
            date={date}
          />

          <TableRow>
            <StyledTableCell colSpan={cells[variant]?.length + 1 || 0} align="center">
              {hasBorder(dayjs(date)) && <Box sx={{ width: '100%', height: 2, bgcolor: 'background.elevation2' }} />}
            </StyledTableCell>
          </TableRow>
        </>
      ) : (
        <StyledTableRow
          className="open"
          sx={{ cursor: 'pointer' }}
          onClick={() => onClickAway()}
          onTouchEnd={handleTouchClose}
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClickAway()
            }
          }}
        >
          <StyledTableCellRound style={{ paddingBottom: 6, paddingTop: 6 }} colSpan={cells[variant]?.length + 1 || 0}>
            <Typography>{dayjs(date).format(DateFormat.DayNumberDayFullName)}</Typography>
          </StyledTableCellRound>
        </StyledTableRow>
      )}

      <TableRow>
        <TableCell
          colSpan={cells[variant]?.length + 1 || 0}
          align="center"
          sx={{ border: '0px', padding: 0, paddingRight: '0px !important' }}
        >
          <AnimatedScheduleCollapse open={isOpen} sx={{ width: '100%', px: 2, pb: 2 }}>
            <ClickAwayListener onClickAway={onClickAway}>
              <Stack
                sx={{
                  border: 1,
                  borderTop: 0,
                  borderColor: 'background.elevation8',
                  width: '100%',
                  borderRadius: '0 0 1em 1em',
                  py: 2,
                  px: { xs: 1, sm: 2, md: 3 },
                  gap: 2,
                }}
              >
                {isBadSchedule && (
                  <Stack direction="row" alignItems="flex-start" gap={1.5} pb={2} pl={1} color="warning.main">
                    <Stack>
                      <Error />
                    </Stack>
                    <Stack>
                      <Typography align="left" fontWeight={700}>
                        {t('page.schedule.mismatched')}
                      </Typography>
                      <Typography align="left">{t('page.schedule.mismatchedDescription')}</Typography>
                      <Stack direction="row" gap={0.5} sx={{ pt: 2 }}>
                        <Typography>{t('page.schedule.mismatchedSum')}</Typography>
                        <Typography fontWeight={800}>{workHours}</Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                )}

                <Stack justifyContent="space-between" direction="row" px={1.5}>
                  <Typography component={'h3'} variant={'h6'} color="text.highEmphasis">
                    {isPlannedHours ? t('page.schedule.description.planned') : t('page.schedule.description.tracked')}
                  </Typography>

                  {isEditable &&
                    !isReadOnly &&
                    !(isPlannedHours && dayjs(date).isBefore(dayjs().format(DateFormat.YearMonthDay))) && (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddInterval}
                      >
                        {t('page.schedule.addInterval')}
                      </Button>
                    )}
                </Stack>

                <Table size="small">
                  <TableHead sx={{ '& .MuiTableCell': { color: 'text.highEmphasis' } }}>
                    <TableRow>
                      <TableCell width="35%">{t('components.table.cell.from')}</TableCell>
                      <TableCell width="35%">{t('components.table.cell.to')}</TableCell>
                      <TableCell width="30%" align="center">
                        <Stack direction="row">
                          <Stack width="80%" alignItems="center" justifyContent="center">
                            {t('components.table.cell.time')}
                          </Stack>
                          <Stack width="20%"></Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {intervals.map(
                      interval =>
                        !interval.notShow && (
                          <TableRow key={interval.id}>
                            <TableCell>
                              {isEditable ? (
                                <TimePicker
                                  inputRef={timePickerRef}
                                  ampm={false}
                                  value={dayjs(interval.from, DateFormat.HourMinute)}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      sx: { width: '100%' },
                                      inputProps: {
                                        'aria-label': 'Select-from-time',
                                      },
                                    },
                                  }}
                                  onChange={e => {
                                    editInterval(interval.id, date, dayjs(e).format(DateFormat.HourMinute), true)
                                  }}
                                />
                              ) : (
                                <Typography variant="body2">{interval.from}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditable ? (
                                <TimePicker
                                  value={dayjs(interval.to, DateFormat.HourMinute)}
                                  ampm={false}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      sx: { width: '100%' },
                                      error: !isToTimeValid(interval),
                                      inputProps: {
                                        'aria-label': 'Select-to-time',
                                      },
                                    },
                                  }}
                                  onChange={e => {
                                    editInterval(interval.id, date, dayjs(e).format(DateFormat.HourMinute), false)
                                  }}
                                  minTime={dayjs(interval.from, DateFormat.HourMinute)}
                                />
                              ) : (
                                <Typography variant="body2">{interval.to}</Typography>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                height: '100%',
                              }}
                            >
                              <Stack direction="row" alignItems="center">
                                <Stack width="75%">{calculateIntervalHours(interval.from, interval.to)}</Stack>
                                <Stack width="25%" justifyContent="center" alignItems="center">
                                  {isEditable && intervals?.length !== undefined && intervals?.length !== 0 && (
                                    <IconButton
                                      aria-label={'Clear-time-period'}
                                      size="small"
                                      onClick={e => {
                                        e.stopPropagation()
                                        deleteInterval(interval.id)
                                      }}
                                      sx={{
                                        color: 'text.base',
                                        transition: 'all 150ms ease-in-out',
                                        ml: 1,
                                        '&:hover': {
                                          color: 'text.highEmphasis',
                                          bgcolor: 'primary.hoverTransparent',
                                        },
                                        '& svg': {
                                          width: 14,
                                          height: 14,
                                        },
                                      }}
                                    >
                                      <Clear />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )
                    )}
                  </TableBody>

                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ border: '0px', borderTop: 1, pt: 2 }}>
                        <Typography sx={{ fontWeight: 700 }}>{t('page.vacationView.sum')}</Typography>
                      </TableCell>
                      <TableCell colSpan={2} align="center" sx={{ border: '0px', borderTop: 1 }}>
                        <Stack direction="row">
                          <Stack width="75%" alignItems="center" justifyContent="center">
                            <Typography sx={{ fontWeight: 700 }}>{allHours}</Typography>
                          </Stack>
                          <Stack width="25%"></Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>

                {!isReadOnly && !(isPlannedHours && dayjs(date).isBefore(dayjs().format(DateFormat.YearMonthDay))) && (
                  <Stack direction="row" py={2} justifyContent="center">
                    {!isEditable ? (
                      <Stack>
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          startIcon={<Edit />}
                          onClick={handleEdit}
                        >
                          {t('page.schedule.edit')}
                        </Button>
                      </Stack>
                    ) : (
                      <Stack direction="row" justifyContent="center" gap={2} width="100%">
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          startIcon={<Clear />}
                          onClick={handleCancel}
                          sx={{ width: '35%' }}
                        >
                          {t('page.schedule.cancel')}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<Check />}
                          onClick={handleSave}
                          sx={{ width: '35%' }}
                          disabled={!isSaveEnabled}
                        >
                          {t('page.schedule.save')}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </ClickAwayListener>
          </AnimatedScheduleCollapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const arePropsEqual = (prev: ScheduleTableSectionProps, next: ScheduleTableSectionProps) => {
  return (
    prev.date === next.date &&
    prev.intervals === next.intervals &&
    prev.isOpen === next.isOpen &&
    prev.isSelected === next.isSelected &&
    prev.isEditable === next.isEditable &&
    prev.isPlannedHours === next.isPlannedHours &&
    prev.variant === next.variant &&
    prev.isTogglable === next.isTogglable &&
    prev.isReadOnly === next.isReadOnly &&
    prev.worklogHours === next.worklogHours
  )
}

export const ScheduleTableSection = React.memo(ScheduleTableSectionComponent, arePropsEqual)

export default ScheduleTableSection
